import React, { useEffect, useState } from "react";
import { Paper, Stack, Typography, Switch, Alert, CircularProgress, Box } from "@mui/material";
import axios from "axios";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import apiEndpoints from "../../../apiconfig";
import { apiErrorMessage } from "./apiErrorMessage";

// Global ON/OFF for sending WhatsApp messages to parents — handy while testing.
// When OFF, every sender skips the Meta API call and logs the message as "skipped".
export default function WhatsAppSendingSwitch({ glass }) {
  const token = localStorage.getItem("token");
  const { showSnackbar } = useSnackbar();
  const [enabled, setEnabled] = useState(null); // null = loading / unknown
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios
      .get(`${apiEndpoints.settings}?action=get_whatsapp`, authHeaders)
      .then((res) => {
        if (typeof res.data === "string") throw new Error("NOT_DEPLOYED");
        if (res.data?.status !== "success") throw new Error(res.data?.message);
        setEnabled(!!res.data.data?.sending_enabled);
      })
      .catch((err) => {
        const msg =
          err?.message === "Invalid action"
            ? "Server API is out of date: upload the latest api/settings.php to use this switch."
            : apiErrorMessage(err, "settings.php", "Failed to load WhatsApp sending status");
        setLoadError(msg);
        showSnackbar(msg, "error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (next) => {
    setSaving(true);
    try {
      const res = await axios.post(
        `${apiEndpoints.settings}?action=save_whatsapp`,
        { sending_enabled: next },
        authHeaders,
      );
      if (typeof res.data === "string") throw new Error("NOT_DEPLOYED");
      if (res.data?.status !== "success") throw new Error(res.data?.message);
      setEnabled(!!res.data.data?.sending_enabled);
      showSnackbar(res.data.message, next ? "success" : "warning");
    } catch (err) {
      showSnackbar(apiErrorMessage(err, "settings.php", "Failed to change WhatsApp sending"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ ...glass, p: { xs: 2, sm: 2.5 }, mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography fontWeight={700}>Send WhatsApp messages</Typography>
          <Typography variant="body2" color="text.secondary">
            Turn off while testing — attendance, announcements, activities, diary, calendar,
            registration and reminder messages will not be sent.
          </Typography>
        </Box>
        {enabled === null && loadError ? (
          <Typography variant="body2" color="error.main" fontWeight={700}>
            Unavailable
          </Typography>
        ) : enabled === null ? (
          <CircularProgress size={22} />
        ) : (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="body2"
              fontWeight={700}
              color={enabled ? "success.main" : "error.main"}
            >
              {enabled ? "ON" : "OFF"}
            </Typography>
            <Switch
              checked={enabled}
              disabled={saving}
              color="success"
              onChange={(e) => toggle(e.target.checked)}
              inputProps={{ "aria-label": "Send WhatsApp messages" }}
            />
          </Stack>
        )}
      </Stack>

      {loadError && (
        <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
          {loadError}
        </Alert>
      )}

      {enabled === false && (
        <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
          WhatsApp sending is OFF. No messages reach parents; each one is recorded in the WhatsApp
          log as <b>skipped</b> so you can see what would have been sent.
        </Alert>
      )}
    </Paper>
  );
}
